import React, { useEffect, useRef, useState } from "react";
import { Select, Input, Row, Col, Spin } from "antd";
import { getRefFieldsById } from "../../../../../../auth/actions/objActions";
import { fetchRefRecords } from "../../../../../../components/Utils/formColumns";
import _ from "lodash";
import IconTriggerModal from "../../Snippets/IconTriggerModal";

const { Option } = Select;

// O formulário de registros lê 1000 registros por vez; aqui eram 20. Só viram
// opção os registros que têm valor no campo de busca, então uma página curta de
// registros antigos (sem valor, porque o campo entrou depois) voltava vazia — e
// como o "acabou" era decidido pelo número de opções, a rolagem nunca ia buscar
// a página seguinte. Página maior + varredura até achar a primeira opção.
const PAGE_SIZE = 100;
const MAX_AUTO_PAGES = 10;

// Definições do campo referenciado: `getValueOf` é o campo de busca da Consulta
// (refLookup) e `refFields` o das seleções referenciadas. Campos antigos podem
// ter só uma das duas listas preenchida, então uma serve de reserva para a
// outra — sem isso o seletor ficava mudo, sem opção nem explicação.
const resolveRefDefinitions = (field, mode) => {
    const primary = mode === "refLookup" ? field?.getValueOf : field?.refFields;
    const fallback = mode === "refLookup" ? field?.refFields : field?.getValueOf;
    const defs = (Array.isArray(primary) && primary.length > 0) ? primary : fallback;
    return (Array.isArray(defs) ? defs : []).filter(def => def && def.form && def.field);
};

export const RefInput = ({ value, onChange, mode, fieldName, fieldLabel, field }) => {

    const [data, setData] = useState([]);
    const [fieldsRef, setFieldsRef] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        pageSize: PAGE_SIZE,
        final: false,
    });
    const [refSource, setRefSource] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [valueSelect, setValueSelect] = useState(undefined);
    const searchTimer = useRef(null);

    const isMultiple = mode === 'refMultipleSelection';
    const currentValue = _.isPlainObject(value) ? value : {};

    // O valor gravado é "<idDoRegistro>@<idDoCampo>" — é o formato que o back-end
    // espera ao criar/atualizar o registro (getRefDataById). O id do campo vem do
    // nome que voltou em cada registro; quando há uma única definição referenciada
    // (sempre o caso da Consulta), ela própria é a reserva. Antes, um nome não
    // encontrado estourava em `undefined._id` e derrubava a lista inteira — todos
    // os registros sumiam por causa de um único item.
    const buildOptions = (options, refFields) => {
        const refFieldByName = _.keyBy(
            (refFields || []).filter(item => item && item.fieldName),
            'fieldName'
        );
        const singleFieldId = refFields.length === 1
            ? (refFields[0]._id ?? refFields[0].field)
            : null;

        return options
            .map(opt => {
                const fieldId = refFieldByName[opt.field]?._id ?? singleFieldId;
                if (!fieldId) return null;
                return { ...opt, value: `${opt.value}@${fieldId}` };
            })
            .filter(Boolean);
    };

    const fetchOptions = async (searchValue, currentPage, refFields = null) => {
        refFields = refFields || fieldsRef;
        if (!refFields || refFields.length === 0) return;
        setLoading(true);
        try {
            let page = currentPage;
            let collected = [];
            let exhausted = false;
            let pagesRead = 0;

            // Segue lendo enquanto o formulário ainda tem registros e nenhuma
            // opção apareceu: registros sem valor no campo de busca não viram
            // opção, e parar na primeira página vazia era o que deixava a caixa
            // com "Não há dados" mesmo havendo registros mais adiante.
            do {
                const { options, total } = await fetchRefRecords({
                    refFields,
                    keyWords: searchValue,
                    page,
                    pageSize: PAGE_SIZE,
                });
                collected = collected.concat(buildOptions(options, refFields));
                exhausted = total < PAGE_SIZE;
                pagesRead += 1;
                page += 1;
            } while (!exhausted && collected.length === 0 && pagesRead < MAX_AUTO_PAGES);

            setLoadError(null);
            setData(prevData =>
                currentPage === 1 ? collected : [...prevData, ...collected]
            );

            setPagination(prev => ({
                ...prev,
                currentPage: page - 1,
                final: exhausted
            }));

        } catch (error) {
            console.error("Error fetching reference fields:", error);
            if (currentPage === 1) {
                setData([]);
                setLoadError("Não foi possível carregar os registros do formulário referenciado.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            const definitions = resolveRefDefinitions(field, mode);

            if (definitions.length === 0) {
                setLoadError("Este campo não tem um campo de busca configurado no formulário de origem.");
                return;
            }

            let resolved = [];
            try {
                const response = await getRefFieldsById({ fields: definitions });
                resolved = (response?.message ?? [])
                    .map((obj) => ({
                        // `_index` já vem convertido ("obj_animal"); `form` volta
                        // sem o prefixo, então serve de reserva reconstruído.
                        form: obj._index || (obj.form ? `obj_${obj.form}` : null),
                        fieldName: obj.field,
                        fieldType: obj.fieldtype,
                        _id: obj._id
                    }))
                    .filter(item => item.form && item.fieldName && item._id);
            } catch (error) {
                console.error("Error fetching reference fields:", error);
            }

            // Se a resolução dos nomes falhar, seguimos com as definições cruas
            // ({ form, field }): o back-end resolve o nome do campo por conta
            // própria quando `fieldName` não é enviado. É o mesmo caminho que o
            // formulário de registros usa, e evita que uma falha aqui deixe o
            // seletor vazio sem motivo aparente.
            const refFieldsToUse = resolved.length > 0
                ? resolved
                : definitions.map(({ form, field: fieldId }) => ({ form, field: fieldId }));

            setRefSource({
                form: (definitions[0].form || "").replace("obj_", ""),
                fieldName: resolved[0]?.fieldName ?? null,
            });
            setFieldsRef(refFieldsToUse);
            await fetchOptions('', 1, refFieldsToUse);
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field?._id, mode]);

    useEffect(() => {
        if (currentValue.value !== undefined && currentValue.value !== null) {
            setInputValue(currentValue.value);
            setValueSelect(currentValue.value);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    useEffect(() => () => clearTimeout(searchTimer.current), []);

    const handleSearchTermChange = value => {
        setSearchTerm(value);
        setPagination(prev => ({ ...prev, final: false, currentPage: 1 }));
        // A busca vai ao servidor a cada tecla; um respiro evita uma requisição
        // por caractere e o resultado de uma busca antiga chegar por último.
        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => fetchOptions(value, 1), 350);
    };

    const handleScroll = event => {
        const { target } = event;
        if (
            target.scrollTop + target.offsetHeight >= target.scrollHeight - 100 &&
            !loading &&
            !pagination.final
        ) {
            const nextPage = pagination.currentPage + 1;
            setPagination(prev => ({ ...prev, currentPage: nextPage }));
            fetchOptions(searchTerm, nextPage);
        }
    };

    // Derivado do valor, não duplicado em estado: o RecordForm popula o
    // formulário num efeito, então a primeira renderização chega sem `value` e
    // um useState travaria em "value". Ao reabrir uma automação salva o seletor
    // mostrava "Anotação" (que lê a prop) e o corpo renderizava o ramo de valor,
    // escondendo a anotação já gravada.
    const inputType = currentValue.type ? currentValue.type : "value";

    const handleInputTypeChange = valueChange => {
        setValueSelect(isMultiple ? [] : undefined);
        setInputValue(null);
        onChange({
            ...currentValue,
            type: valueChange,
            // Só a seleção múltipla guarda lista; Consulta e seleção única
            // guardam um valor só, e um `[]` aqui virava valor selecionado.
            value: isMultiple ? [] : null,
            fieldType: field?.fieldtype,
        });
    };

    const handleInputChange = e => {
        setInputValue(e.target.value);
        onChange({
            fieldType: field?.fieldtype,
            type: inputType,
            value: e.target.value,
        });
    };

    const handleSelectChange = (valueChange) => {
        setValueSelect(valueChange);
        onChange({
            ...currentValue,
            fieldType: field?.fieldtype,
            type: inputType,
            value: valueChange ?? null,
        });
    }

    const modeSelect = isMultiple ? "multiple" : undefined;

    // A anotação de seleção referenciada é a mesma dos demais campos —
    // `{{ trigger.Campo.0.value }}`. Antes o editor abria em modo código
    // (`isCode`) e sugeria a expressão JavaScript `trigger["Campo"][0].value`,
    // porque o valor gravado é `valor@idDoCampo`: isso deixava um único tipo de
    // campo com sintaxe própria, e quem montava a automação tinha de saber qual
    // campo pedia qual formato. O que resta de específico é o aviso de que a
    // anotação precisa vir do registro de origem — o campo em si aceita
    // digitação como os outros.
    const isStructured = mode === 'refMultipleSelection' || mode === 'refUniqueSelection';

    // Mantém o valor já gravado visível mesmo quando ele não está na página de
    // registros carregada (a lista vem paginada).
    const selectedValues = _.compact(_.isArray(valueSelect) ? valueSelect : [valueSelect]);
    const options = _.unionBy(
        data,
        selectedValues.map(item => ({ label: String(item), value: item })),
        'value'
    );

    const notFoundContent = loading
        ? <div style={{ padding: 8, textAlign: "center" }}><Spin size="small" /></div>
        : undefined;

    // Lista vazia com o formulário todo lido não é o mesmo que erro: diz qual
    // formulário e qual campo foram consultados, para o autor da automação
    // conferir a configuração do campo em vez de encarar um "Não há dados".
    const emptyHint = (!loading && !loadError && data.length === 0 && pagination.final && refSource)
        ? `Nenhum registro de "${refSource.form}"${refSource.fieldName ? ` tem valor em "${refSource.fieldName}"` : ""}.`
        : null;

    return (
        <Row gutter={[8, 8]} align="middle">
            <Col flex="120px">
                <Select
                    style={{ width: "100%" }}
                    onChange={handleInputTypeChange}
                    value={inputType}
                >
                    <Option value="value">Valor</Option>
                    <Option value="binding">Anotação</Option>
                </Select>
            </Col>
            <Col flex="auto" style={{ minWidth: 160 }}>
                {inputType === 'value' ? (
                    <>
                        <Select
                            // Sem `showSearch` o antd não monta a caixa de digitação
                            // na seleção simples e ignora `onSearch` — a Consulta
                            // ficava com a lista presa nos 20 primeiros registros e
                            // sem nenhuma forma de digitar o que se procura.
                            showSearch
                            allowClear
                            mode={modeSelect}
                            value={valueSelect}
                            loading={loading}
                            style={{ width: '100%' }}
                            placeholder="Pesquise o registro"
                            onSearch={handleSearchTermChange}
                            onPopupScroll={handleScroll}
                            options={options}
                            filterOption={false}
                            notFoundContent={notFoundContent}
                            onChange={handleSelectChange}
                        />
                        {loadError ? (
                            <div className="ae-hint ae-hint--error">{loadError}</div>
                        ) : null}
                        {emptyHint ? (
                            <div className="ae-hint">{emptyHint}</div>
                        ) : null}
                    </>
                ) : (
                    <>
                        <Input
                            value={inputValue}
                            onChange={handleInputChange}
                            style={{ width: '100%' }}
                            addonAfter={<IconTriggerModal fieldName={`${fieldName}.value`} fieldLabel={fieldLabel} />}
                        />
                        {isStructured ? (
                            <div className="ae-hint">
                                A anotação precisa apontar para um campo do registro de origem — ex.:{" "}
                                <code>{"{{ trigger.Campo.0.value }}"}</code>. Use o raio para montá-la.
                            </div>
                        ) : null}
                    </>
                )}
            </Col>
        </Row>
    );
};
